import React from "react";
import { HStack, VStack } from "@chakra-ui/react";
import { SERIAL_NUMBER_REGEXPS } from "@constants";
import { useGetStepsByStepName } from "@hooks";
import { useAuth } from "@hooks";
import { StepFrontend } from "@constants";
import { useStoreModuleStatus } from "@hooks";
import { PRODUCTION_STEPS_PS, PRODUCTION_STEPS_2S } from "@constants";

import {
  MakeInventoryReportsModal,
  MakeFailureReportsModal,
  MakeMetrologyReportsModal,
  MakeAssemblyReportsModal,
  Loading,
} from "@components";

import { Layout } from "./PageLayouts";

export function MakeReportsPage() {
  const { documents: metrologySteps2S } = useGetStepsByStepName(
    "2S_SANDWICH_METROLOGY_TEST",
  );
  const { documents: metrologyStepsPS } = useGetStepsByStepName(
    "PS_TEST_SANDWICH_METROLOGY",
  );
  const { documents: glueSteps2S } = useGetStepsByStepName("2S_GLUE_SANDWICH");
  const { documents: glueHybridSteps2S } =
    useGetStepsByStepName("2S_GLUE_HYBRID");
  const { documents: glueStepsPS } = useGetStepsByStepName(
    "PS_SANDWICH_HYBRID_GLUING",
  );

  const stepListBaseplates = [
    PRODUCTION_STEPS_PS.STEP_PS_CF_BASEPLATE_KAPTON_GLUING,
    PRODUCTION_STEPS_PS.STEP_PS_MA_PSA_BASEPLATE_GLUING,
  ];
  const stepListPSS = [
    PRODUCTION_STEPS_PS.STEP_PS_GLUE_HV_TAIL_TO_SENSOR,
    PRODUCTION_STEPS_PS.STEP_PS_ENCAPSULATE_HV_TAIL,
    PRODUCTION_STEPS_PS.STEP_PS_SENSOR_IV_MEASURMENT,
    PRODUCTION_STEPS_PS.STEP_PS_SENSOR_SPACER_GLUING,
  ];
  const stepList2S = [
    PRODUCTION_STEPS_2S.STEP_2S_SENSOR_KAPTON_GLUING,
    PRODUCTION_STEPS_2S.STEP_2S_SENSOR_HV_TAIL_GLUING,
    PRODUCTION_STEPS_2S.STEP_2S_SENSOR_BOND_AND_ENCAPSULATE_HV_TAIL,
    PRODUCTION_STEPS_2S.STEP_2S_SENSOR_IV_MEASURMENT,
    PRODUCTION_STEPS_2S.STEP_2S_GLUE_SANDWICH,
  ];
  const stepListMapsa = [
    PRODUCTION_STEPS_PS.STEP_PS_TEST_MA_PSA,
    PRODUCTION_STEPS_PS.STEP_PS_MA_PSA_BASEPLATE_GLUING,
  ];
  const store = useStoreModuleStatus();
  const { user } = useAuth();

  const location = user?.location ?? "Brown";

  const modulesForBaseplateSteps = React.useMemo(() => {
    const stepLists = new Array<Array<string>>();
    if (store === undefined) {
      return stepLists;
    }
    for (const step of stepListBaseplates) {
      const cModulesForStep = store.modulesForStep[step] ?? [];
      const moduleList = new Array<string>();
      for (const module of cModulesForStep) {
        if ((store.partsByLocation[location] ?? []).includes(module)) {
          if (module.match(SERIAL_NUMBER_REGEXPS.MODULE_PS.CFBASEPLATE)) {
            moduleList.push(module);
          }
        }
      }
      stepLists.push(moduleList);
    }

    return stepLists;
  }, [store, location, stepListBaseplates]);

  const modulesForPSSSteps = React.useMemo(() => {
    const stepLists = new Array<Array<string>>();
    if (store === undefined) {
      return stepLists;
    }
    for (const step of stepListPSS) {
      const cModulesForStep = store.modulesForStep[step] ?? [];
      const moduleList = new Array<string>();
      for (const module of cModulesForStep) {
        if ((store.partsByLocation[location] ?? []).includes(module)) {
          moduleList.push(module);
        }
      }
      stepLists.push(moduleList);
    }

    return stepLists;
  }, [store, location]);

  const modulesFor2SSteps = React.useMemo(() => {
    const stepLists = new Array<Array<string>>();
    if (store === undefined) {
      return stepLists;
    }
    for (const step of stepList2S) {
      const cModulesForStep = store.modulesForStep[step] ?? [];
      const moduleList = new Array<string>();
      for (const module of cModulesForStep) {
        if ((store.partsByLocation[location] ?? []).includes(module)) {
          moduleList.push(module);
        }
      }
      stepLists.push(moduleList);
    }

    return stepLists;
  }, [store]);

  const modulesForMapsaSteps = React.useMemo(() => {
    const stepLists = new Array<Array<string>>();
    if (store === undefined) {
      return stepLists;
    }
    for (const step of stepListMapsa) {
      const cModulesForStep = store.modulesForStep[step] ?? [];
      const moduleList = new Array<string>();
      for (const module of cModulesForStep) {
        if ((store.partsByLocation[location] ?? []).includes(module)) {
          if (module.match(SERIAL_NUMBER_REGEXPS.MODULE_PS.MAPSA)) {
            moduleList.push(module);
          }
        }
      }
      stepLists.push(moduleList);
    }

    return stepLists;
  }, [store]);

  return (
    <Layout variant="centered">
      {metrologySteps2S || metrologyStepsPS ? (
        <VStack gap="32px">
          <HStack w="100%" justifyContent="space-around">
            <MakeMetrologyReportsModal
              metrologySteps2S={metrologySteps2S as StepFrontend[]}
              metrologyStepsPS={metrologyStepsPS as StepFrontend[]}
              glueSteps2S={glueSteps2S as StepFrontend[]}
              glueHybridSteps2S={glueHybridSteps2S as StepFrontend[]}
              glueStepsPS={glueStepsPS as StepFrontend[]}
            />
            <MakeInventoryReportsModal
              modulesForBaseplateSteps={modulesForBaseplateSteps}
              modulesFor2SSteps={modulesFor2SSteps}
              modulesForPSSSteps={modulesForPSSSteps}
              modulesForMapsaSteps={modulesForMapsaSteps}
            />
            <MakeFailureReportsModal />
            <MakeAssemblyReportsModal />
          </HStack>
        </VStack>
      ) : (
        <Loading />
      )}
    </Layout>
  );
}
